// Hovering a city dot: the pulse ring on the map, the readout card beside it,
// and the click that opens the briefing.
//
// The readout is where a dot stops being a colour and says what it measured —
// the reading in plain language, and for an activity the ✓/~/✗ breakdown of the
// drivers behind its score, so a "62" is never just a number.
//
// setupHover takes the open-city action rather than importing it. This module
// is about reading a dot; what a click then DOES is the app's decision, and
// injecting it is what keeps this side of the arrow free of the city panel (the
// same reason myday.ts takes a context).

import { $ } from '../dom.ts';
import { map } from '../map/instance.ts';
import { state } from '../state.ts';
import { NO_HOVER } from './breakpoints.ts';
import { VAR, factorsFor } from '../../lib/variables.ts';
import { placeLabel } from '../../lib/city-stats.ts';
import { whyHtml } from '../../lib/city-view.ts';
import type { VarKey } from '../../lib/variables.ts';
import type { Recipe } from '../../lib/recipe.ts';
import type { CityProps, CityHint } from '../../contracts/city.ts';
import type { MapMouseEvent } from 'maplibre-gl';
import type { Point } from 'geojson';

// What a click on a dot should do. Supplied by the composition root.
export interface HoverDeps {
  onOpenCity: (id: number, hint?: CityHint | null) => void;
}

// ── warming the briefing under the cursor ────────────────────────────────────
//
// A reader who hovers a dot usually clicks it, and /city/:id/series is answered
// with a day's cache — so fetching it while the tooltip is being read means the
// click resolves out of the browser cache with no round trip at all.
//
// This is only defensible because the series is now a ~1 KB read off the baked
// table (see lib/series-format.ts). Against the grid sweep it replaced, warming
// every dot the cursor crossed would have been hundreds of milliseconds of
// origin CPU each and would have made the map markedly WORSE, not better.
const PREFETCH_DELAY_MS = 120;
const prefetched = new Set<string>();
let prefetchTimer: ReturnType<typeof setTimeout> | null = null;

function prefetchSeries(id: number | null) {
  // The mousemove handler already returns early on touch, but this is the one
  // side effect here that costs the network, so it states its own condition:
  // MapLibre synthesises mousemove from taps, and warming every dot a finger
  // scrolls past is a lot of requests for a click that is not coming.
  if (id == null || NO_HOVER.matches) return;
  const key = `${id}|${state.bucket}`;
  if (prefetched.has(key)) return;
  if (prefetchTimer) clearTimeout(prefetchTimer);
  // Debounced: sweeping the cursor across a dense band should warm where it
  // came to rest, not every dot on the way.
  prefetchTimer = setTimeout(() => {
    if (state.hoverId !== id || prefetched.has(key)) return;
    prefetched.add(key);
    // Low priority and unawaited — this must never compete with the tiles the
    // reader is actually looking at, and a failure is simply a cache miss later.
    fetch(`/city/${id}/series?bucket=${state.bucket}`, { priority: 'low' } as RequestInit).catch(() => {
      prefetched.delete(key);
    });
  }, PREFETCH_DELAY_MS);
}

function fmtReading(v: VarKey, val: number) {
  const m = VAR[v];
  if (m.bands) {
    const band = m.bands.find(([lt]) => val < lt)!;
    const gloss = m.gloss ? `${m.gloss} ` : '';
    return `${band[1]} <i>(${gloss}${val} ${m.unit})</i>`;
  }
  if (m.score) return `${Math.round(val)}/100`;
  return `${val} ${m.unit}`;
}
// `recipe` is consulted only for the custom rating, whose drivers depend on it.
export function activityBlock(v: VarKey, props: CityProps, recipe: Recipe) {
  const m = VAR[v];
  const val = props[v] as number | null | undefined;
  const head = `<div class="tt-sep"></div><div class="tt-act"><span>${m.label}</span>`;
  if (val === undefined || val === null) return `${head}<b>no data</b></div>`;
  const band = m.bands!.find(([lt]) => val < lt)![1];
  // The same score details every other transient surface shows, in the same
  // state — see whyHtml. A tooltip is the smallest of them, which is exactly why
  // it should not carry four rows unless the reader has said they want them.
  const why = whyHtml(factorsFor(v, recipe), (k) => props[k] as number | null | undefined, state.whyExpanded);
  return `${head}<b>${band}</b></div>${why}`;
}

let hoverCoords: [number, number] | null = null;
// Where the open briefing's city is, when there is one to point at.
function openCityCoords(): [number, number] | null {
  if (state.view !== 'city') return null;
  const sel = state.selectedCity;
  const s = state.series;
  if (s && (!sel || s.id === sel.id)) return [s.lon, s.lat];
  return sel && sel.lon != null && sel.lat != null ? [sel.lon, sel.lat] : null;
}

// The pulse marks ONE place: the dot under the pointer, or — with nothing
// hovered — the city whose briefing is open. A small city's static ring is easy
// to lose among its neighbours' dots; the pulse is what says "this one". It
// yields to a hover, so the two never pulse at once.
export function positionRing() {
  const ring = $('cityring');
  const hovering = state.hoverId != null && !!hoverCoords;
  const at = hovering ? hoverCoords : openCityCoords();
  if (!at) {
    ring.hidden = true;
    return;
  }
  // The open city's own pulse is the larger, with a solid centre: it has to be
  // found, not merely confirmed under a pointer that is already on it.
  ring.classList.toggle('is-open', !hovering);
  // map.project() is relative to the map container; the ring is position:fixed (viewport
  // space). When the map isn't full-screen (the city-view band, shifted by scroll), add
  // the container's viewport offset so the ring stays on the dot. In the full-screen map
  // the offset is (0,0), so this is a no-op there.
  const p = map.project(at);
  const rect = map.getContainer().getBoundingClientRect();
  const x = rect.left + p.x, y = rect.top + p.y;
  // The ring is fixed to the viewport, over everything. Once the band has
  // scrolled away (or the point is outside it) there is no dot under it to mark.
  if (!hovering && (x < rect.left || x > rect.right || y < Math.max(0, rect.top) || y > rect.bottom)) {
    ring.hidden = true;
    return;
  }
  ring.style.left = `${x}px`;
  ring.style.top = `${y}px`;
  ring.hidden = false;
}

function tooltipHtml(props: CityProps) {
  const esc = (s: unknown) => String(s == null ? '' : s).replace(/[&<>]/g, (c) => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }) as Record<string, string>)[c]);
  const temp = props.t != null ? `${props.t} °C` : '—';
  const rain = props.rain != null ? `${Math.round(props.rain)}%` : '—';
  const act = VAR[state.variable] && VAR[state.variable].score ? activityBlock(state.variable, props, state.recipe) : '';
  return (
    `<div class="tt-name">${esc(props.name)}</div>` +
    `<div class="tt-country">${esc(placeLabel(props.country, props.admin))}</div>` +
    `<div class="tt-readings"><div><span>Temp</span> <b>${temp}</b></div><div><span>Rain</span> <b>${rain}</b></div></div>` +
    act +
    `<div class="tt-open">OPEN CITY BRIEFING →</div>`
  );
}

export function setupHover({ onOpenCity }: HoverDeps) {
  const tip = $('tooltip');
  const hoverLayers = () => ['weather-points', 'city-labels'].filter((id) => map.getLayer(id));
  map.on('mousemove', (e) => {
    if (state.view === 'landing') return;
    // MapLibre synthesises mousemove from taps — on touch devices that would flash
    // the tooltip under the finger just before the tap opens the city. No hover,
    // no tooltip; the tap goes straight through the click handlers below.
    if (NO_HOVER.matches) return;
    const hit = map.queryRenderedFeatures(e.point, { layers: hoverLayers() })[0];
    if (!hit) {
      map.getCanvas().style.cursor = '';
      tip.hidden = true;
      state.hoverId = null;
      hoverCoords = null;
      positionRing();
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
    tip.innerHTML = tooltipHtml(hit.properties as CityProps);
    tip.hidden = false;
    positionTip(tip, e.point);
    state.hoverId = (hit.properties as CityProps).id;
    hoverCoords = (hit.geometry as Point).coordinates as [number, number];
    positionRing();
    prefetchSeries(state.hoverId);
  });
  map.getCanvas().addEventListener('mouseleave', () => {
    map.getCanvas().style.cursor = '';
    tip.hidden = true;
    state.hoverId = null;
    hoverCoords = null;
    positionRing();
  });
  // Keep the ring glued to the dot while the map pans/zooms.
  map.on('move', positionRing);

  // Click a dot (or its label) → open that city's briefing.
  const openFromEvent = (e: MapMouseEvent) => {
    const hit = map.queryRenderedFeatures(e.point, { layers: hoverLayers() })[0];
    if (!hit) return;
    const p = hit.properties as CityProps;
    const [lon, lat] = (hit.geometry as Point).coordinates;
    onOpenCity(p.id, { name: p.name, nameAscii: p.nameAscii, country: p.country, admin: p.admin, citySlug: p.citySlug, pop: p.pop, mz: p.mz, lon, lat });
  };
  map.on('click', 'weather-points', openFromEvent);
  map.on('click', 'city-labels', openFromEvent);
}

function positionTip(tip: HTMLElement, point: { x: number; y: number }) {
  const pad = 16;
  // point is map-container-relative; the tooltip is position:fixed. Add the container's
  // viewport offset (see positionRing) so it tracks the cursor when the map is a band.
  const rect = map.getContainer().getBoundingClientRect();
  const px = rect.left + point.x;
  const py = rect.top + point.y;
  const w = tip.offsetWidth;
  const h = tip.offsetHeight;
  let x = px + pad;
  let y = py + pad;
  if (x + w > window.innerWidth) x = px - w - pad;
  if (y + h > window.innerHeight) y = py - h - pad;
  tip.style.transform = `translate(${x}px, ${y}px)`;
}
